import React, { useState, useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import { VITE_BACKEND_SOCKET_IO } from '../config/config';

export default function PaymentQRDisplay({ paymentType, amount, orderId, onSuccess }) {
  const [qrString, setQrString] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [referenceId, setReferenceId] = useState(null);

  const onSuccessRef = useRef(onSuccess);
  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  useEffect(() => {
    let isMounted = true;
    let pollInterval = null;

    if (!paymentType?.qr_config) {
      setLoading(false);
      return;
    }

    const initQR = async () => {
      try {
        let config;
        try {
          config = JSON.parse(paymentType.qr_config);
        } catch (e) {
          // Plain string fallback
          if (isMounted) {
            setQrString(paymentType.qr_config);
            setLoading(false);
          }
          return;
        }
        
        if (config.type === 'static') {
          if (isMounted) {
            setQrString(config.qr_string);
            setLoading(false);
          }
          return;
        }

        if (config.type === 'dynamic') {
          // Fetch dynamic QR from backend
          const token = localStorage.getItem('__acct');
          const res = await axios.post(`${VITE_BACKEND_SOCKET_IO}/api/v1/payments/generate-dynamic-qr`, {
            payment_type_id: paymentType.id,
            amount: amount,
            order_id: orderId
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });

          if (isMounted && res.data.success) {
            setQrString(res.data.qr_string);
            setReferenceId(res.data.reference_id);
            setLoading(false);

            // Start polling
            pollInterval = setInterval(async () => {
              try {
                const statusRes = await axios.post(`${VITE_BACKEND_SOCKET_IO}/api/v1/payments/check-status`, {
                  payment_type_id: paymentType.id,
                  reference_id: res.data.reference_id
                }, {
                  headers: { Authorization: `Bearer ${token}` }
                });

                if (statusRes.data.status === 'success') {
                  clearInterval(pollInterval);
                  if (isMounted && onSuccessRef.current) {
                    onSuccessRef.current();
                  }
                }
              } catch (e) {
                console.error("Error polling payment status", e);
              }
            }, 3000);
          }
        } else {
          if (isMounted) {
            setError("Invalid QR configuration");
            setLoading(false);
          }
        }
      } catch (e) {
        if (isMounted) {
          setQrString(paymentType.qr_config);
          setLoading(false);
        }
      }
    };

    initQR();

    return () => {
      isMounted = false;
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [paymentType?.id, paymentType?.qr_config, amount, orderId]);

  if (loading) {
    return <div className="text-gray-400 text-sm">Generating QR Code...</div>;
  }

  if (error || !qrString) {
    return <div className="text-red-400 text-sm">{error || "Failed to load QR"}</div>;
  }

  return (
    <div className="flex flex-col items-center">
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <QRCodeSVG value={qrString} size={180} level="H" includeMargin={true} />
      </div>
      {referenceId && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <div className="w-4 h-4 border-2 border-restro-green border-t-transparent rounded-full animate-spin"></div>
          <span className="text-sm text-restro-green font-medium animate-pulse">Waiting for payment...</span>
        </div>
      )}
    </div>
  );
}
